import { useCallback, useMemo } from 'react';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { FeatureFlagStrategy, useAppFlagStore } from '@frontend/shared';
import { useAppScopeStore } from './store';

export const useScopedAppFlagStore = () => {
  const { selectedLocationIds } = useAppScopeStore();
  const {
    getFeatureFlagValue,
    getCustomizationFlagValue,
    getCustomizationFlag,
    getFeatureFlag,
    customizationFlagMap,
    getCustomizationFlagLocationMap,
    getFeatureFlagLocationMap,
    featureFlags,
  } = useAppFlagStore();

  const getScopedFeatureFlagValue = useCallback(
    (flagName: string, strategy: FeatureFlagStrategy = 'ANY') =>
      getFeatureFlagValue({ locationIds: selectedLocationIds, flagName, strategy }),
    [selectedLocationIds, getFeatureFlagValue]
  );

  const getScopedCustomizationFlagValue = useCallback(
    (flagName: string, state: keyof typeof State = 'ACTIVE', strategy: FeatureFlagStrategy = 'ANY') =>
      getCustomizationFlagValue({ locationIds: selectedLocationIds, flagName, strategy, state }),
    [selectedLocationIds, getCustomizationFlagValue]
  );

  const getScopedCustomizationFlag = useCallback(
    (flagName: string) => getCustomizationFlag({ locationIds: selectedLocationIds, flagName }),
    [selectedLocationIds, getCustomizationFlag]
  );

  const getScopedFeatureFlag = useCallback(
    (flagName: string) => getFeatureFlag({ locationIds: selectedLocationIds, flagName }),
    [selectedLocationIds, getFeatureFlag]
  );

  const getScopedCustomizationFlagLocationMap = useCallback(
    (flagName: string) => getCustomizationFlagLocationMap({ locationIds: selectedLocationIds, flagName }),
    [selectedLocationIds, getCustomizationFlagLocationMap]
  );

  const getScopedFeatureFlagLocationMap = useCallback(
    (flagName: string) => getFeatureFlagLocationMap({ locationIds: selectedLocationIds, flagName }),
    [selectedLocationIds, getFeatureFlagLocationMap]
  );

  const getScopedCustomizationFlagValueById = useCallback(
    (flagId: string, state: keyof typeof State = 'ACTIVE', strategy: FeatureFlagStrategy = 'ANY') =>
      customizationFlagMap[flagId]
        ? getCustomizationFlagValue({
            locationIds: selectedLocationIds,
            flagName: customizationFlagMap[flagId],
            strategy,
            state,
          })
        : false,
    [selectedLocationIds, customizationFlagMap, getCustomizationFlagValue]
  );

  const getScopedCustomizationFlagById = useCallback(
    (flagId: string) =>
      getCustomizationFlag({ locationIds: selectedLocationIds, flagName: customizationFlagMap[flagId] }),
    [selectedLocationIds, customizationFlagMap, getCustomizationFlag]
  );

  const isFlagStoreLoaded = useMemo(
    () => selectedLocationIds.some((id) => featureFlags[id] !== undefined),
    [selectedLocationIds, featureFlags]
  );

  return {
    getFeatureFlagValue: getScopedFeatureFlagValue,
    getCustomizationFlagValue: getScopedCustomizationFlagValue,
    getCustomizationFlag: getScopedCustomizationFlag,
    getFeatureFlag: getScopedFeatureFlag,
    getCustomizationFlagLocationMap: getScopedCustomizationFlagLocationMap,
    getFeatureFlagLocationMap: getScopedFeatureFlagLocationMap,
    getCustomizationFlagValueById: getScopedCustomizationFlagValueById,
    getCustomizationFlagById: getScopedCustomizationFlagById,
    isFlagStoreLoaded,
  };
};
